import { ReactiveMap } from "@solid-primitives/map"
import { batch, createEffect } from "solid-js"
import eightDirections from "../../lib/core/direction/eightDirections"
import getNextEightDirection from "../../lib/core/direction/getNextEightDirection"
import type { TileGraphNode } from "../../lib/core/engine/TileGraphNode/TileGraphNode.type"
import { isPlainObjDeeplyEqual } from "../../lib/isPlainObjDeeplyEqual"
import { immutableEmptyArray } from "../../lib/lib"
import createNodeNeighborAvgElevationListV2 from "../../lib/three/objects/TerrainMesh1V2/createNodeNeighborAvgElevationListV2"
import createSharedCachedFactory from "./createSharedCachedFactory"
import { useNodeNeighborIndexMap } from "./useNodeNeighborIndexMap"
import { useTerrainBaseWaterElevationListMemo } from "./useTerrainBaseWaterElevationListMemo"

export type NeighborElevationCollection = {
  /** Base elevation of the center tile */
  base: number
  /** Elevations of 8 neighbors in eightDirections order (length === 8) */
  list: number[]  // length === 8
  /** Pre-computed averages for interpolation (length === 8) */
  avgs: number[]  // length === 8
}

// this is a work-around for a bug
// the 3x3 grid of tiles that are updated when a tile changes
// seems to be correct in one place, but wrong in another.
const ALWAYS_REBUILD = false

const FULL_RECALC_PARTIAL_APPLY = true

/**
 * @deprecated use `node.wec()` instead
 */
export const useNodeNeighborBaseWaterElevationReactiveMap = createSharedCachedFactory((engine) => {
  const getNodeNeighborIndexMap = useNodeNeighborIndexMap(engine)
  const getWaterElevationList = useTerrainBaseWaterElevationListMemo(engine)
  // const getWaterProximityKindList = useTerrainWaterProximityKindListMemo(engine)

  const map = new ReactiveMap<TileGraphNode['index'], NeighborElevationCollection>()

  createEffect((prevList: Float32Array | undefined) => {
    const eList: Float32Array = getWaterElevationList()
    // const wpkList: Uint8Array = getWaterProximityKindList()
    const len0 = eList.length
    prevList ||= immutableEmptyArray as unknown as Float32Array
    const len1 = prevList.length
    const nodeNeighborByDirectionMap = getNodeNeighborIndexMap()
    // const nodeNeighborByDirectionMapValues = nodeNeighborByDirectionMap.values()

    function make(index: TileGraphNode['index']): NeighborElevationCollection {
      const base = eList[index]

      // Build neighbor elevation list in eightDirections order: [N, NE, E, SE, S, SW, W, NW]
      const neighborBases : Array<TileGraphNode['index']> = []

      for (let dirIndex = 0; dirIndex < 8; dirIndex++) {
        const dir = eightDirections[dirIndex]
        const neighborIndex = nodeNeighborByDirectionMap.get(dir)![index]

        // Handle missing neighbors with fallback strategy
        if (neighborIndex === -1) {
          // For diagonal neighbors (odd indices: NE, SE, SW, NW)
          if (dirIndex % 2 === 1) {
            // Try first adjacent cardinal direction (e.g., NE -> N)
            const dir1 = getNextEightDirection(dir, 1)
            const neighborIndex1 = nodeNeighborByDirectionMap.get(dir1)![index]
            if (neighborIndex1 !== -1) {
              neighborBases[dirIndex] = eList[neighborIndex1]
              continue
            }

            // Try second adjacent cardinal direction (e.g., NE -> E)
            const dir2 = getNextEightDirection(dir, -1)
            const neighborIndex2 = nodeNeighborByDirectionMap.get(dir2)![index]
            if (neighborIndex2 !== -1) {
              neighborBases[dirIndex] = eList[neighborIndex2]
              continue
            }

            // Both adjacent directions missing, use base elevation
            neighborBases[dirIndex] = base
            continue
          }

          // For cardinal neighbors (even indices: N, E, S, W), use base elevation
          neighborBases[dirIndex] = base
          continue
        }

        // Neighbor exists, use its elevation
        neighborBases[dirIndex] = eList[neighborIndex]
      }

      // Pre-compute averaged elevations for smooth interpolation
      // const avgs = createNodeNeighborAvgElevationList(base, neighborBases)
      const avgs = createNodeNeighborAvgElevationListV2(base, neighborBases)

      const collection = {
        base,
        list: neighborBases,
        avgs,
      }
      return collection
    }

    // Batch all map updates to minimize reactive cascades
    batch(() => {
      // Handle terrain size changes (tiles added/removed)
      if (ALWAYS_REBUILD) {
        // Complete rebuild - clear map and recreate all collections
        map.clear()
        for (let i = 0; i < len0; i++) {
          map.set(i, make(i))
        }
      } else if (FULL_RECALC_PARTIAL_APPLY) {
        // clear only on size change
        if (len0 !== len1) {
          map.clear()
        }
        for (let i = 0; i < len0; i++) {
          // always recreate
          const newValue = make(i)
          const oldValue = map.get(i)
          // only trigger signal if different
          if (!isPlainObjDeeplyEqual(newValue, oldValue)) {
            map.set(i, newValue)
          }
        }
      } else {
        throw new Error('partial apply not implemented')
      }
    })

    // return the elevation list for use as next prevList
    return eList
  })

  return map
})